import React from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@telus-uds/components-web';

/**
 * Sum the engine pricing hook's `selections` into grand totals. Each selection
 * value carries `{ monthly, oneTime, threeYearEasyPayment, fiveYearEasyPayment }`
 * (getVulcanPriceSelections / getConsolidationSelectionTotals); missing keys count
 * as 0 so partial/empty selections are safe.
 */
export const sumPricingSelections = (selections = {}) =>
  Object.values(selections || {}).reduce(
    (acc, sel) => {
      if (sel && typeof sel === 'object') {
        acc.monthly += Number(sel.monthly) || 0;
        acc.oneTime += Number(sel.oneTime) || 0;
        acc.threeYearEasyPayment += Number(sel.threeYearEasyPayment) || 0;
        acc.fiveYearEasyPayment += Number(sel.fiveYearEasyPayment) || 0;
      }
      return acc;
    },
    { monthly: 0, oneTime: 0, threeYearEasyPayment: 0, fiveYearEasyPayment: 0 }
  );

/**
 * Default pricing footer for `<VulcanFormStepRunner>` — the presentational half of
 * the lifecycle the engine already computes. `<FormRunner>` calls `renderFooter`
 * with the engine and exposes the pricing hook's output on `engine.pricingResult`
 * (`{ summary, selections }`). This renders the running monthly / one-time totals
 * from `selections`. A caller can override the whole footer via the harness's
 * `renderFooter` prop (e.g. to add the download-quote CTA / the full summary
 * sections — deeper parity).
 *
 * @param {Object} props
 * @param {{ selections?: Object }} [props.pricingResult]
 */
export function VulcanPricingFooter({ pricingResult }) {
  const totals = sumPricingSelections(
    pricingResult && pricingResult.selections
  );
  const money = n => `$${n.toFixed(2)}`;
  return (
    <div data-vulcan-pricing-footer="true">
      <Typography>{`Monthly: ${money(totals.monthly)}`}</Typography>
      <Typography>{`One-time: ${money(totals.oneTime)}`}</Typography>
    </div>
  );
}
VulcanPricingFooter.propTypes = {
  pricingResult: PropTypes.shape({ selections: PropTypes.shape({}) })
};
VulcanPricingFooter.defaultProps = { pricingResult: null };

export default VulcanPricingFooter;
